import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import React from "react";

import { useLoginController } from "../hooks";
import { cn } from "../../../lib/utils";
import { AuthFormProps } from "../types";
import { AuthCard, AuthLayout, FormError, PasswordInput } from "../components";
import { Label } from "@radix-ui/react-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Login: React.FC<AuthFormProps> = ({ className }) => {
  const controller = useLoginController();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refId = searchParams.get("referringId");

  useEffect(() => {
    if (refId) {
      localStorage.setItem("referringId", refId);
    }
  }, [refId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await controller.login();
  };

  const navigateToReset = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/app/reset");
  };

  const navigateToRegister = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/app/register");
  };

  const renderEmailField = () => (
    <div className="grid gap-2">
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        placeholder="your@email.com"
        disabled={controller.loading}
        onChange={(e) => controller.setEmail(e.target.value)}
        value={controller.email}
        autoComplete="email"
        className={cn(controller.formEmailError ? "border-destructive" : "")}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            controller.login();
          }
        }}
      />
      <FormError message={controller.formEmailError} />
    </div>
  );

  const renderPasswordField = () => (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="password">Password</Label>
        {/* <Button
          variant="link"
          className="p-0 h-auto text-sm"
          onClick={navigateToReset}
        >
          Forgot password?
        </Button> */}
      </div>
      <PasswordInput
        id="password"
        placeholder="Enter password"
        disabled={controller.loading}
        onChange={(e) => controller.setPassword(e.target.value)}
        value={controller.password}
        autoComplete="current-password"
        error={controller.formPasswordError}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            controller.login();
          }
        }}
      />
      <FormError message={controller.formPasswordError} />
    </div>
  );

  return (
    <AuthLayout>
      <AuthCard
        title="Sign in"
        description="Enter your credentials below to access your account"
        className={className}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex flex-col gap-4">
            {renderEmailField()}
            {renderPasswordField()}

            <Button
              type="submit"
              className="w-full"
              disabled={controller.loading}
            >
              {controller.loading ? "Signing in..." : "Sign in"}
            </Button>
          </div>
        </form>
        <div className="mt-4 text-center text-sm">
          Don't have an account?{" "}
          <Button
            variant="link"
            className="p-0 h-auto text-sm"
            onClick={navigateToRegister}
          >
            Create account
          </Button>
        </div>
      </AuthCard>
    </AuthLayout>
  );
};
